/**
 * BulkJobProgress — shared progress UX for long-running bulk AI jobs.
 *
 * Renders the trigger button (disabled + spinner + live "X/N" while the job
 * runs) AND a floating bottom-right progress card with a progress bar,
 * percent, ETA, current record, completion summary, error reasons and a
 * "Retry failed only" action. Status is DB-backed server-side, so progress
 * survives page refreshes and pod restarts — the component resumes polling
 * automatically if a job is already running when it mounts.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2, X, RotateCcw, CheckCircle2, AlertTriangle } from 'lucide-react';

const fmtEta = (s) => {
  if (s == null) return null;
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `~${m}m ${sec}s left` : `~${sec}s left`;
};

export const BulkJobProgress = ({
  label, startUrl, statusUrl, retryUrl, startPayload = {},
  onDone, confirmMessage, buttonClassName = '', icon: Icon = Sparkles,
  testIdPrefix = 'bulk-job', unit = 'records', variant = 'outline',
}) => {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const pollRef = useRef(null);
  const wasRunning = useRef(false);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const handleFinished = (s) => {
    stopPoll();
    if (!wasRunning.current) return; // don't toast for stale historical jobs
    wasRunning.current = false;
    const failed = s.errors || 0;
    if (s.status === 'failed') {
      toast.error(s.error || 'The job failed. You can re-run it — completed records are skipped.', { duration: 10000 });
    } else {
      toast.success(
        `${(s.total || 0).toLocaleString()} ${unit} processed · ${(s.enriched || 0).toLocaleString()} enriched` +
        (failed ? ` · ${failed} failed` : ''),
        { duration: 8000 },
      );
    }
    onDone && onDone(s);
  };

  const poll = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(statusUrl, { withCredentials: true });
        setStatus(data);
        if (data.running) { wasRunning.current = true; setCardOpen(true); }
        else if (wasRunning.current) handleFinished(data);
        else stopPoll();
      } catch { /* transient poll errors are ignored */ }
    }, 2500);
  };

  // Resume an in-flight job after a page refresh.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(statusUrl, { withCredentials: true });
        setStatus(data);
        if (data.running) { wasRunning.current = true; setCardOpen(true); poll(); }
      } catch { /* ignore */ }
    })();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUrl]);

  const start = async () => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setStarting(true); setCardOpen(true);
    try {
      const { data } = await axios.post(startUrl, startPayload, { withCredentials: true });
      const jobTotal = data?.total_firms ?? data?.total;
      if (data?.status === 'already_running') {
        toast.info('A job is already running — progress shown below.');
        wasRunning.current = true;
        poll();
      } else if (jobTotal === 0) {
        toast.info(`Nothing to process — all ${unit} are already complete.`);
        setCardOpen(false);
      } else {
        wasRunning.current = true;
        poll();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start the job');
      setCardOpen(false);
    } finally { setStarting(false); }
  };

  const retryFailed = async () => {
    setStarting(true); setCardOpen(true);
    try {
      const { data } = await axios.post(retryUrl, {}, { withCredentials: true });
      if (data.total === 0) toast.info('Nothing to retry.');
      else { wasRunning.current = true; poll(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start retry');
    } finally { setStarting(false); }
  };

  const running = !!status?.running;
  const pct = running && status.total > 0 ? Math.round((status.completed / status.total) * 100) : 0;
  const finishedWithFailures = !running && status && (status.errors || 0) > 0 && (status.failed_ids || []).length > 0;

  return (
    <>
      <Button
        variant={variant}
        onClick={start}
        disabled={running || starting}
        className={buttonClassName}
        data-testid={`${testIdPrefix}-start-btn`}
      >
        {(running || starting) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2 text-emerald-600" />}
        {starting ? 'Initializing enrichment…'
          : running ? `Enriching ${status.completed}/${status.total}`
          : label}
      </Button>

      {cardOpen && (running || starting || status) && (
        <div className="fixed bottom-4 right-4 z-[80] w-[340px] bg-white border border-slate-200 rounded-xl shadow-xl p-4"
             data-testid={`${testIdPrefix}-progress-card`}>
          <div className="flex items-start justify-between mb-1.5">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              {running || starting
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-600" />
                : (status?.errors ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />)}
              {label}
            </p>
            <button onClick={() => setCardOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid={`${testIdPrefix}-close-card`}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {starting && !running && <p className="text-xs text-slate-500">Preparing AI jobs…</p>}

          {running && (
            <>
              <p className="text-xs text-slate-600 mb-1">
                Enriching <strong>{status.completed}</strong> of <strong>{status.total}</strong> · {pct}%
                {status.eta_seconds != null && <span className="text-slate-400"> · {fmtEta(status.eta_seconds)}</span>}
              </p>
              {status.current && <p className="text-[11px] text-slate-400 truncate mb-2">Now: {status.current}</p>}
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-violet-600 h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5">{status.enriched || 0} enriched · {status.skipped || 0} skipped · {status.errors || 0} failed</p>
            </>
          )}

          {!running && !starting && status && (
            <>
              <p className="text-xs text-slate-600">
                {status.status === 'failed'
                  ? <span className="text-red-600">{status.error || 'Job failed.'}</span>
                  : <>{(status.total || 0).toLocaleString()} processed · <span className="text-emerald-700 font-medium">{status.enriched || 0} enriched</span>{status.errors ? <> · <span className="text-red-600 font-medium">{status.errors} failed</span></> : null}</>}
              </p>
              {(status.error_details || []).slice(0, 3).map((e, i) => (
                <p key={i} className="text-[10px] text-red-500 truncate mt-0.5">• {e.name}: {e.reason}</p>
              ))}
              {finishedWithFailures && retryUrl && (
                <Button size="sm" variant="outline" onClick={retryFailed} className="mt-2 h-7 text-xs" data-testid={`${testIdPrefix}-retry-failed-btn`}>
                  <RotateCcw className="w-3 h-3 mr-1.5" /> Retry {status.failed_ids.length} failed only
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default BulkJobProgress;
